import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listPartnerItems, listPartners, type PartnerDto } from '@/lib/api/core';

/**
 * usePartnerProducts — catálogo de la Tienda partners vía
 * `GET /v1/partners/catalog-items` (api-core, `type=product`).
 *
 * El endpoint `catalog-items` solo expone `partner_id`/`category_id` como uuid
 * (sin nombre de partner ni slug de categoría legible). Por eso cruzamos con
 * `GET /v1/partners` para resolver el `partner_id` a su `name` real y a su
 * `type` (cocteleria / concierto / gastronomia / …), que usamos como faceta de
 * categoría legible para el badge y los filtros. `partner_id` sin match →
 * "First Class" como atribución de fallback.
 */

const CATEGORY_LABELS: Record<string, string> = {
  bebidas: 'Bebidas',
  mixologia: 'Mixología',
  cocteleria: 'Coctelería',
  gastronomia: 'Gastronomía',
  restaurante: 'Restaurante',
  concierto: 'Conciertos',
  evento: 'Eventos',
  experiencias: 'Experiencias',
  experiencia: 'Experiencias',
  reservas: 'Reservas',
  merchandising: 'Merchandising',
  retail: 'Retail',
  lifestyle: 'Lifestyle',
  servicios: 'Servicios',
  hotel: 'Hotelería',
  bar: 'Bar',
};

/**
 * Normaliza un `type`/slug de categoría a una clave estable (sin acentos,
 * minúsculas, sin espacios) para agrupar y filtrar.
 */
function categorySlug(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return 'otros';
  return trimmed
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Etiqueta legible de una categoría de producto a partir de su slug. */
export function partnerCategoryLabel(slug: string): string {
  const key = categorySlug(slug);
  return CATEGORY_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export type PartnerProduct = {
  id: string;
  title: string;
  description: string | null;
  priceCents: number;
  category: string;
  categoryLabel: string;
  stock: number;
  imageUrl: string | null;
  partnerId: string;
  partnerName: string;
};

export type PartnerCategory = { slug: string; label: string };

/** Categorías únicas presentes en un set de productos, ordenadas alfabéticamente. */
export function deriveCategories(products: ReadonlyArray<PartnerProduct>): PartnerCategory[] {
  const seen = new Map<string, string>();
  for (const p of products) {
    if (!seen.has(p.category)) seen.set(p.category, p.categoryLabel);
  }
  return [...seen.entries()]
    .map(([slug, label]) => ({ slug, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

function toWholeCop(value: unknown): number {
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

const FALLBACK_PARTNER_NAME = 'First Class';

/** Índice `partner_id → PartnerDto` para resolver nombre y tipo en O(1). */
function indexPartners(partners: ReadonlyArray<PartnerDto>): Map<string, PartnerDto> {
  const byId = new Map<string, PartnerDto>();
  for (const p of partners) {
    if (typeof p.id === 'string' && p.id) byId.set(p.id, p);
  }
  return byId;
}

async function fetchPartnerProducts(): Promise<PartnerProduct[]> {
  // Productos y directorio de partners en paralelo (sin waterfall). Si la
  // lista de partners falla, degradamos a fallback sin romper la tienda.
  const [items, partners] = await Promise.all([
    listPartnerItems(),
    listPartners().catch(() => [] as PartnerDto[]),
  ]);
  const byId = indexPartners(partners);

  return items
    .filter((r) => r.is_active !== false)
    .map((r) => {
      const price = toWholeCop(r.price_firstclass ?? r.price_public);
      const stockRaw = Number(r.stock);
      const partner = typeof r.partner_id === 'string' ? byId.get(r.partner_id) : undefined;

      // Categoría legible a partir del `type` del partner (cocteleria,
      // concierto, gastronomia, …). Sin partner/type → "Otros".
      const rawType = typeof partner?.type === 'string' ? partner.type : '';
      const category = rawType ? categorySlug(rawType) : 'otros';

      const partnerName =
        typeof partner?.name === 'string' && partner.name.trim()
          ? partner.name.trim()
          : FALLBACK_PARTNER_NAME;

      const partnerImage =
        typeof partner?.main_image === 'string' && partner.main_image.trim()
          ? partner.main_image.trim()
          : typeof partner?.cover_image === 'string' && partner.cover_image.trim()
            ? partner.cover_image.trim()
            : typeof partner?.logo_url === 'string' && partner.logo_url.trim()
              ? partner.logo_url.trim()
              : '';
      const itemImage =
        typeof (r as Record<string, unknown>).image_url === 'string'
          ? String((r as Record<string, unknown>).image_url).trim()
          : '';
      const image = itemImage || partnerImage;

      return {
        id: String(r.id),
        title: typeof r.name === 'string' && r.name.trim() ? r.name : 'Producto',
        description: (r.description as string | null) ?? null,
        priceCents: price,
        category,
        categoryLabel: category === 'otros' ? 'Otros' : partnerCategoryLabel(category),
        stock: Number.isFinite(stockRaw) ? stockRaw : 0,
        imageUrl: image || null,
        partnerId: (r.partner_id as string | null) ?? 'first-class',
        partnerName,
      };
    });
}

export function usePartnerProducts(): UseQueryResult<PartnerProduct[]> {
  return useQuery({
    queryKey: ['partner-products', 'active'],
    queryFn: fetchPartnerProducts,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
